
import { useQuery } from '@apollo/client';
import EDPopover from '../../../../../../../../../../../BaseComponents/Popover/EDPopover';
import { makeStyles } from 'tss-react/mui';
import {
  USE_NEW_AS_START_ANALYS_ID,
  EKAnalysGroupingModeType,
} from '../EkonomiskAnalysFavoriteAnalysDialog';
import EDStandardSelect from '../../../../../../../../../../../BaseComponents/Selects/StandardSelect/EDStandardSelect';
import { IStartAnalysInfo } from '../Queries/ChangeStartAnalysMutation/ChangeStartAnalysMutation';
import { ISelection } from '../../../../../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import EDCheckbox from '../../../../../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import KombKodbehCountQuery from '../EkonomiskAnalysKodbehCount.gql';
import {
  EkonomiskAnalysKodbehCountVariables as IKombKodbehVariables,
  EkonomiskAnalysKodbehCount as IKombKodbehData,
} from '../_QueryTypes/EkonomiskAnalysKodbehCount';

interface IProps {
  fullscreen?: boolean;
  open: boolean;
  anchorEl?: HTMLElement;
  onClose: () => void;
  groupingMode: EKAnalysGroupingModeType;
  onGroupingModeChange: (newValue: EKAnalysGroupingModeType) => void;
  showUseNewReportAsStart: boolean;
  startAnalys?: IStartAnalysInfo;
  updateStartAnalys: (analysInfo?: IStartAnalysInfo) => void;
  availableKodbeh: ISelection[];
  dbname: string;
  codesystemId: string;
  kombKodbehVisibility: IKombKodbehVisibility;
  updateKombKodbehVisibility: IUpdateKombKodbehVisibiltyFnType;
  updateStartAnalysAtLogin: boolean;
  setUpdateStartAnalysAtLogin: (update: boolean) => void;
}

export interface IKombKodbehVisibility {
  hideKombRes: boolean;
  hideKombBal: boolean;
  hideKombBlandat: boolean;
  hideIngRes: boolean;
  hideIngBal: boolean;
  hideIngBlandat: boolean;
  useKombKodbeh: boolean;
}

export type IUpdateKombKodbehVisibiltyFnType = (
  visibility: IKombKodbehVisibility
) => void;

const useStyles = makeStyles()({
  popover: {
    padding: '24px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
});

const EkonomiskAnalysFavoriteOptionsMenu: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();

  const queryResult = useQuery<IKombKodbehData, IKombKodbehVariables>(
    KombKodbehCountQuery,
    {
      variables: {
        dbname: props.dbname,
        kodplanid: props.codesystemId,
      },
    }
  );

  const newAnalysAsStartAnalys =
    props.startAnalys?.rapportId === USE_NEW_AS_START_ANALYS_ID;

  const onNyAnalysSomStartAnalysCheck = (checked: boolean) => {
    props.updateStartAnalys(
      !checked
        ? undefined
        : {
            rapportId: USE_NEW_AS_START_ANALYS_ID,
            idKod: undefined,
            utdelningLopnr: 1,
            kodbehorighet:
              props.availableKodbeh.find(
                kodbeh => kodbeh.name === 'Hela huvudboken'
              )?.id ?? '',
            kodplanId: undefined,
          }
    );
    props.setUpdateStartAnalysAtLogin(false);
  };

  return (
    <EDPopover
      open={props.open}
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      classes={{
        paper: classes.popover,
      }}
    >
      <div className={classes.container}>
        {props.showUseNewReportAsStart && (
          <EDCheckbox
            checked={newAnalysAsStartAnalys}
            label="Ny analys som startanalys"
            onCheck={onNyAnalysSomStartAnalysCheck}
          />
        )}
        <EDCheckbox
          label="Analysera startanalys vid inloggning"
          checked={props.updateStartAnalysAtLogin}
          onCheck={props.setUpdateStartAnalysAtLogin}
          disabled={newAnalysAsStartAnalys}
          disabledReason={
            newAnalysAsStartAnalys
              ? 'Endast aktuellt val om startanalysen inte är en ny analys'
              : undefined
          }
        />
        {props.kombKodbehVisibility.useKombKodbeh && (
          <>
            {queryResult.data?.ekanalysantalingkodbeh &&
              queryResult.data?.ekanalysantalingkodbeh.antal_balans > 1 && (
                <EDCheckbox
                  disabled={props.kombKodbehVisibility.hideIngBal}
                  disabledReason="Kan inte dölja både kombinerade och ingående balansbehörigheter samtidigt"
                  checked={!props.kombKodbehVisibility.hideKombBal}
                  label="Visa min balansbehörighet"
                  onCheck={checked =>
                    props.updateKombKodbehVisibility({
                      ...props.kombKodbehVisibility,
                      hideKombBal: !checked,
                      hideIngBal: !checked
                        ? false
                        : props.kombKodbehVisibility.hideIngBal,
                    })
                  }
                />
              )}
            {queryResult.data?.ekanalysantalingkodbeh &&
              queryResult.data?.ekanalysantalingkodbeh.antal_blandat > 1 && (
                <EDCheckbox
                  disabled={props.kombKodbehVisibility.hideIngBlandat}
                  disabledReason="Kan inte dölja både kombinerade och ingående blandade behörigheter samtidigt"
                  checked={!props.kombKodbehVisibility.hideKombBlandat}
                  label="Visa min blandade behörighet"
                  onCheck={checked =>
                    props.updateKombKodbehVisibility({
                      ...props.kombKodbehVisibility,
                      hideKombBlandat: !checked,
                      hideIngBlandat: !checked
                        ? false
                        : props.kombKodbehVisibility.hideIngBlandat,
                    })
                  }
                />
              )}
            {queryResult.data?.ekanalysantalingkodbeh &&
              queryResult.data?.ekanalysantalingkodbeh.antal_resultat > 1 && (
                <EDCheckbox
                  disabled={props.kombKodbehVisibility.hideIngRes}
                  disabledReason="Kan inte dölja både kombinerade och ingående resultatbehörigheter samtidigt"
                  checked={!props.kombKodbehVisibility.hideKombRes}
                  label="Visa min resultatbehörighet"
                  onCheck={checked =>
                    props.updateKombKodbehVisibility({
                      ...props.kombKodbehVisibility,
                      hideKombRes: !checked,
                      hideIngRes: !checked
                        ? false
                        : props.kombKodbehVisibility.hideIngRes,
                    })
                  }
                />
              )}

            {queryResult.data?.ekanalysantalingkodbeh &&
              queryResult.data?.ekanalysantalingkodbeh.antal_balans > 1 && (
                <EDCheckbox
                  disabled={props.kombKodbehVisibility.hideKombBal}
                  disabledReason="Kan inte dölja både kombinerade och ingående balansbehörigheter samtidigt"
                  checked={!props.kombKodbehVisibility.hideIngBal}
                  label="Visa mina ingående balansbehörigheter"
                  onCheck={checked =>
                    props.updateKombKodbehVisibility({
                      ...props.kombKodbehVisibility,
                      hideIngBal: !checked,
                    })
                  }
                />
              )}
            {queryResult.data?.ekanalysantalingkodbeh &&
              queryResult.data?.ekanalysantalingkodbeh.antal_blandat > 1 && (
                <EDCheckbox
                  disabled={props.kombKodbehVisibility.hideKombBlandat}
                  disabledReason="Kan inte dölja både kombinerade och ingående blandade behörigheter samtidigt"
                  checked={!props.kombKodbehVisibility.hideIngBlandat}
                  label="Visa mina ingående blandade behörigheter"
                  onCheck={checked =>
                    props.updateKombKodbehVisibility({
                      ...props.kombKodbehVisibility,
                      hideIngBlandat: !checked,
                    })
                  }
                />
              )}
            {queryResult.data?.ekanalysantalingkodbeh &&
              queryResult.data?.ekanalysantalingkodbeh.antal_resultat > 1 && (
                <EDCheckbox
                  disabled={props.kombKodbehVisibility.hideKombRes}
                  disabledReason="Kan inte dölja både kombinerade och ingående resultatbehörigheter samtidigt"
                  checked={!props.kombKodbehVisibility.hideIngRes}
                  label="Visa mina ingående resultatbehörigheter"
                  onCheck={checked =>
                    props.updateKombKodbehVisibility({
                      ...props.kombKodbehVisibility,
                      hideIngRes: !checked,
                    })
                  }
                />
              )}
          </>
        )}
        <EDStandardSelect
          label="Sortera på"
          value={props.groupingMode}
          items={[
            { header: 'Kodbehörighet', value: 'kodbeh' },
            {
              header: 'Rapportkategori',
              value: 'category',
              testId: 'Rapportkategori',
            },
          ]}
          onChange={props.onGroupingModeChange}
          margin="none"
          fullWidth={false}
        />
      </div>
    </EDPopover>
  );
};

export default EkonomiskAnalysFavoriteOptionsMenu;
